import type React from 'react';
import { useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import Typography from 'app/ui/components/atoms/typography';
import Button from 'app/ui/components/atoms/button';
import VideoPlayer from 'app/ui/components/molecules/video-component';

import ChevronRight from 'app/ui/components/atoms/icons/chevron-right';
import { ComponentAlignment, type Video } from 'app/types/graphql';
import { trackEvent } from 'src/app/utils/analytics/amplitude/tracker';

type ResourceSectionDetailsProps = {
  id: string;
  title: string;
  mediaFirst: boolean;
  content?: string | null;
  imageUrl?: string;
  video?: Video | null;
  documentUrl?: string;
  linkUrl?: string;
  componentAlignment: ComponentAlignment;
};

const Wrapper = styled.div<{ $mediaFirst: boolean; $isVertical: boolean }>`
  display: flex;
  align-items: center;

  margin: 0;

  ${({ $isVertical }) => `flex-direction: ${$isVertical ? 'column-reverse' : 'column'}`};

  ${({ theme: { media, spacing }, $mediaFirst, $isVertical }) =>
    `${media.medium(
      () => `
        margin: 0;
        gap: ${$isVertical ? spacing(8) : '5rem'};

        ${$isVertical ? 'width: 48.75rem; margin: 0 auto;' : `flex-direction: ${$mediaFirst ? 'row' : 'row-reverse'}`};
    `,
    )}`};
`;

const TextWrapper = styled.div<{ $isVertical?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  ${({ $isVertical }) => $isVertical && 'align-items: center;'}
`;

const TitleWrapper = styled.div<{ $isVertical?: boolean }>`
  margin: 0 0 1.5rem 0;
  ${({ theme: { media } }) => `${media.medium(() => 'margin: 0 0 0.6rem')}`};

  ${({ $isVertical }) => $isVertical && 'text-align: center;'}
`;

const TextContentWrapper = styled.div<{ $isVertical?: boolean }>`
  margin: 0 0 1.5rem 0;
  ${({ theme: { media } }) => `${media.medium(() => 'margin: 0.6rem 0')}`};
  ${({ $isVertical }) => $isVertical && 'text-align: center;'}

  ul {
    padding-left: ${({ theme: { spacing } }) => spacing(4)};
    margin-left: 0;
  }
`;

const MediaWrapper = styled.div<{ $isVertical: boolean }>`
  display: flex;
  flex: ${({ $isVertical }) => ($isVertical ? 'none' : '1')};
  align-items: start;
`;

const ButtonWrapper = styled.div`
  margin: 0 0 1.25rem;
  ${({ theme: { media } }) => `${media.medium(() => 'margin: 0.6rem 0;')}`};
`;

const MobileButtonWrapper = styled(ButtonWrapper)<{ $isVertical?: boolean }>`
  margin: 1.25rem 0 1.25rem;
  ${({ $isVertical }) => $isVertical && 'align-self: center;'}
  ${({ theme: { media } }) => {
    return `
        ${media.medium(
          () => `
            display: none;
            `,
        )}
        `;
  }}
`;

const DesktopButtonWrapper = styled(ButtonWrapper)<{ $isVertical?: boolean }>`
  display: none;
  ${({ $isVertical }) => $isVertical && 'align-self: center;'}
  ${({ theme: { media } }) => {
    return `
        ${media.medium(
          () => `
            display: block;
            `,
        )}
        `;
  }}
`;

const StyledImage = styled.img<{ $isVertical: boolean }>`
  ${({ $isVertical }) =>
    $isVertical ? 'width: auto; max-width: 160px; max-height: 80px; object-fit: contain;' : 'width: 100%;'}
  margin: ${({ $isVertical, theme: { spacing } }) => ($isVertical ? `0 auto ${spacing(6)}` : '0')};

  ${({ theme: { media }, $isVertical }) =>
    media.medium(
      () => `
      ${$isVertical ? 'width: auto; max-width: 200px; max-height: 100px; object-fit: contain;' : 'width: 100%; min-width: 400px;'}
    `,
    )};
`;

const ChevronIcon = styled(ChevronRight)`
  padding-top: 0.25rem;
  width: 1rem;
  height: 1rem;
`;

const ImageTracker = ({ resource_id, resource_title }: { resource_id: string; resource_title: string }) => {
  useEffect(() => {
    trackEvent('resource_viewed', {
      resource_id,
      resource_title,
      resource_type: 'image',
    });
  }, [resource_id, resource_title]);

  return null;
};

const ResourceSectionDetails: React.FC<ResourceSectionDetailsProps> = ({
  id,
  title,
  content,
  imageUrl,
  video,
  documentUrl,
  linkUrl,
  mediaFirst,
  componentAlignment,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const documentLabel = intl.formatMessage({ id: 'resource.buttons.document' });
  const folderLabel = intl.formatMessage({ id: 'resource.buttons.folder' });

  const isVertical = componentAlignment === ComponentAlignment.VERTICAL;

  return (
    <Wrapper $mediaFirst={mediaFirst} $isVertical={isVertical}>
      <TextWrapper $isVertical={isVertical}>
        <TitleWrapper $isVertical={isVertical}>
          <Typography variant="h3">{title}</Typography>
        </TitleWrapper>
        <TextContentWrapper $isVertical={isVertical}>
          <Typography variant="body1" htmlContent={content || ''} />
        </TextContentWrapper>
        {documentUrl && (
          <DesktopButtonWrapper $isVertical={isVertical}>
            <Button
              label={documentLabel}
              type="secondary-white"
              onClick={() => {
                trackEvent('resource_viewed', {
                  resource_id: id,
                  resource_title: title,
                  resource_type: 'document',
                });
                window?.open(documentUrl, '_blank');
              }}
              icon={<ChevronIcon color={theme.colors.white} />}
              fullWidthMob={true}
            />
          </DesktopButtonWrapper>
        )}
        {linkUrl && (
          <DesktopButtonWrapper $isVertical={isVertical}>
            <Button
              label={folderLabel}
              type="secondary-white"
              onClick={() => {
                trackEvent('resource_viewed', {
                  resource_id: id,
                  resource_title: title,
                  resource_type: 'link',
                });
                window?.open(linkUrl, '_blank');
              }}
              icon={<ChevronIcon color={theme.colors.white} />}
              fullWidthMob={true}
            />
          </DesktopButtonWrapper>
        )}
      </TextWrapper>
      {(video?.signedUrl || imageUrl) && (
        <MediaWrapper $isVertical={isVertical}>
          {video?.signedUrl ? (
            <VideoPlayer
              src={video.signedUrl}
              title=""
              duration=""
              showRewindAndForward={true}
              trackEventOnClickPlay={() =>
                trackEvent('resource_viewed', {
                  resource_id: id,
                  resource_title: title,
                  resource_type: 'video',
                })
              }
              thumbnail={video.thumbnail?.signedUrl}
              subtitle={video.subtitles}
              isCentered={true}
            />
          ) : (
            <>
              <ImageTracker resource_id={id} resource_title={title} />
              <StyledImage $isVertical={isVertical} src={imageUrl} />
            </>
          )}
        </MediaWrapper>
      )}
      {documentUrl && (
        <MobileButtonWrapper $isVertical={isVertical}>
          <Button
            label={documentLabel}
            type="secondary-white"
            onClick={() => {
              trackEvent('resource_viewed', {
                resource_id: id,
                resource_title: title,
                resource_type: 'document',
              });
              window.location.href = documentUrl;
            }}
            icon={<ChevronIcon color={theme.colors.white} />}
            fullWidthMob={true}
          />
        </MobileButtonWrapper>
      )}
      {linkUrl && (
        <MobileButtonWrapper $isVertical={isVertical}>
          <Button
            label={folderLabel}
            type="secondary-white"
            onClick={() => {
              trackEvent('resource_viewed', {
                resource_id: id,
                resource_title: title,
                resource_type: 'link',
              });
              window.location.href = linkUrl;
            }}
            icon={<ChevronIcon color={theme.colors.white} />}
            fullWidthMob={true}
          />
        </MobileButtonWrapper>
      )}
    </Wrapper>
  );
};

export default ResourceSectionDetails;
